import AdvanceDataTable from '@/components/core/advance-data-table/advance-data-table';
import { ConfirmationModal } from '@/components/core/confirmation-modal/confirmation-modal';
import { LinkedRecordBar } from '@/components/core';
import { Skeleton } from '@/components/ui-kit/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { useHasPermission } from '@/hooks/use-has-permission';
import { useLinkedRecord } from '@/hooks/use-linked-record';
import { useServerTable } from '@/hooks/use-server-table';
import { mergeWhere } from '@/lib/table-query';
import { dateOnlyRangeClause } from '@/lib/utils/custom-date/custom-date';
import { leaveSheetTermLabel, useGetLeaveSheetTerms } from '@/modules/time-off/team-time-off';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ToilSummaryCards } from '../toil-summary-cards/toil-summary-cards';
import { createToilTableColumns } from '../toil-table-columns/toil-table-columns';
import { DEFAULT_TOIL_REQUESTS_ORDER } from '../../graphql/queries';
import { useCurrentUserId, useGetToilRequests, useWithdrawToilRequest } from '../../hooks/use-toil';
import { ToilRequest, ToilRequestsPagingResult } from '../../types/toil.types';

/**
 * The TOIL Requests tab of My Time Off (the former My TOIL page): the signed-in user's TOIL summary and
 * requests of one HR term. Request TOIL sits in the page header.
 */
export const MyToilTab = () => {
  const { t } = useTranslation();
  const userId = useCurrentUserId();
  // Like legacy, the list shows one HR term (the current one); only managers may pick another.
  const canPickTerm = useHasPermission(HRM_PERMISSIONS.TIME_OFF.TOIL_VIEW_ALL);
  const canRequest = useHasPermission(HRM_PERMISSIONS.TIME_OFF.TOIL_REQUEST);
  const [termId, setTermId] = useState<string | undefined>(undefined);
  const [withdrawTarget, setWithdrawTarget] = useState<ToilRequest | null>(null);

  // A notification's link lists only its request, whichever HR term it is in.
  const { linkedId, clearLinked } = useLinkedRecord();
  const { pagination, resetPage, order, columnWhere, tableProps } = useServerTable({
    pageSize: 15,
    defaultOrder: DEFAULT_TOIL_REQUESTS_ORDER,
    linkedId,
  });

  const { data: terms, isLoading: isTermsLoading } = useGetLeaveSheetTerms(userId);
  const selectedTerm =
    terms?.find((term) => term.ItemId === termId) ??
    terms?.find((term) => term.isCurrent) ??
    terms?.[0];
  const termRange = selectedTerm
    ? dateOnlyRangeClause(selectedTerm.periodStart, selectedTerm.periodEnd)
    : undefined;

  const filter = linkedId ? { UserId: userId } : { UserId: userId, ToilDate: termRange };
  const canList = Boolean(termRange) || Boolean(linkedId);
  const {
    data,
    isLoading: isRequestsLoading,
    error,
  } = useGetToilRequests(
    {
      pageNo: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      filter: mergeWhere(filter, columnWhere),
      order,
    },
    canList
  );
  const isLoading = (isTermsLoading && !linkedId) || (canList && isRequestsLoading);
  const result = (data as { ToilRequests?: ToilRequestsPagingResult })?.ToilRequests;
  const items = result?.items ?? [];
  const totalCount = result?.totalCount ?? 0;

  const { mutateAsync: withdrawToilRequest, isPending: isWithdrawing } = useWithdrawToilRequest();

  const handleTermChange = (next: string) => {
    setTermId(next);
    resetPage();
  };

  const handleWithdrawConfirm = async () => {
    if (!withdrawTarget) return;
    try {
      await withdrawToilRequest(withdrawTarget.ItemId);
    } catch {
      // Error toast is already shown by the mutation's global error handler.
    } finally {
      setWithdrawTarget(null);
    }
  };

  const columns = createToilTableColumns({
    t,
    onWithdraw: setWithdrawTarget,
    canWithdraw: canRequest,
  });

  const termPicker = isTermsLoading ? (
    <Skeleton className="h-9 w-full md:w-56" />
  ) : (
    selectedTerm &&
    terms && (
      <Select value={selectedTerm.ItemId} onValueChange={handleTermChange} disabled={!canPickTerm}>
        <SelectTrigger className="w-full md:w-56" aria-label={t('SELECT_HR_TERM')}>
          <SelectValue placeholder={t('SELECT_HR_TERM')} />
        </SelectTrigger>
        <SelectContent>
          {terms.map((term) => (
            <SelectItem key={term.ItemId} value={term.ItemId}>
              {leaveSheetTermLabel(term, t)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    )
  );

  return (
    <div className="flex w-full flex-col gap-4">
      {/* The HR term, at the end of the tab's first line (full width on a phone). */}
      <div className="flex justify-end">{termPicker}</div>

      {/* The cards need the HR term: the same placeholders as theirs while the terms load. */}
      {isTermsLoading && (
        <div className="grid grid-cols-[repeat(auto-fit,minmax(14rem,1fr))] gap-3">
          {Array.from({ length: 3 }, (_, index) => (
            <Skeleton key={index} className="h-20 rounded-lg" />
          ))}
        </div>
      )}
      {selectedTerm && <ToilSummaryCards userId={userId} term={selectedTerm} />}

      <LinkedRecordBar linkedId={linkedId} onShowAll={clearLinked} />
      <AdvanceDataTable
        {...tableProps}
        data={items}
        columns={columns}
        isLoading={isLoading}
        error={error instanceof Error ? error : null}
        pagination={{
          pageIndex: pagination.pageIndex,
          pageSize: pagination.pageSize,
          totalCount,
        }}
        isExpandRowContent={false}
        mobileView="cards"
      />

      <ConfirmationModal
        open={Boolean(withdrawTarget)}
        onOpenChange={(open) => !open && setWithdrawTarget(null)}
        title={t('WITHDRAW_TOIL_REQUEST')}
        description={t('TOIL_REQUEST_WITHDRAW_WARNING')}
        confirmText={isWithdrawing ? t('WITHDRAWING') : t('WITHDRAW')}
        preventAutoClose={isWithdrawing}
        onConfirm={handleWithdrawConfirm}
      />
    </div>
  );
};
